import {
  DivisionsTree,
  Office,
  WL_PRESENCE_OOO,
  WL_PRESENCE_REMOTE,
  DivisionType
} from '@gettactic/api';
import { FlatOfficeItem } from './FlatOfficeItem';
import { LocationVariants } from './utils';

import { FilterProvider } from '@cmp-src/controls/FilterContext/FilterContext';
import {
  Select,
  SelectClearTrigger,
  SelectContent,
  SelectGroup,
  SelectSeparator
} from '@cmp-src/primitives/Select/Select';
import {
  SelectSearch,
  FilterInputHandle
} from '@cmp-src/primitives/Select/select-controls/SelectSearch';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { OfficeSelectContent } from './OfficeSelectContent';
import { cn } from '@gettactic/helpers';
import { SelectButton } from '@cmp-src/primitives/Select/select-controls/SelectButton';

const ROOT_ID = '0';

export type ExtendedDivisionTree = DivisionsTree & {
  total_desks: number;
  total_open_desks: number;
};
export type ExtendedOffice = Office;
export type PossibleDivisionNode =
  | DivisionsTree
  | ExtendedDivisionTree
  | {
      type: DivisionType;
      id: Office['id'];
      name: Office['name'];
      offices: Office[];
      children: [];
      parent_id: null;
    };

/**
 * Internal utility function to transform offices into PossibleDivisionNode format
 */
const createTopLevelOfficeNodes = (
  offices: Office[]
): PossibleDivisionNode[] => {
  return offices.map((office) => ({
    id: office.id,
    name: office.name,
    type: 'top_level_office' as DivisionType,
    parent_id: null,
    sort_order: office.weight || 0,
    offices: [],
    children: []
  }));
};

export type OfficeSelectProps = {
  items?: PossibleDivisionNode[];
  divisions?: {
    tree: DivisionsTree;
  };
  defaultValue?: string;
  showRemote?: boolean;
  showOOO?: boolean;
  triggerClassName?: string;
  triggerIcon?: React.ReactNode;
  onClear?: () => void;
  scrollViewportClassname?: string;
  showSelectedLabel?: boolean;
};

export type OfficeSelectContentProps = {
  allItems: PossibleDivisionNode[];
  currentDir: PossibleDivisionNode | null;
  onOpenDir: (dirId: NodeId) => void;
  onBack: () => void;
  showCapacity?: boolean;
  setNoFilteredResults: (value: boolean) => void;
};

export type NodeId = ExtendedDivisionTree['id'];

export const OfficeSelect = ({
  items: itemsProp,
  divisions,
  defaultValue: defaultValueProp,
  showRemote = true,
  showOOO = true,
  triggerIcon,
  scrollViewportClassname,
  showSelectedLabel = true,
  ...props
}: OfficeSelectProps & SelectPrimitive.SelectProps) => {
  // Transform divisions data into items if provided
  const items = React.useMemo(() => {
    if (itemsProp) {
      return itemsProp;
    }

    if (divisions?.tree) {
      const allOfficeNodes = divisions.tree.children || [];
      const topLevelOffices = divisions.tree.offices || [];

      if (topLevelOffices.length > 0) {
        return [
          ...allOfficeNodes,
          ...createTopLevelOfficeNodes(topLevelOffices)
        ];
      }

      return allOfficeNodes;
    }

    return [];
  }, [itemsProp, divisions]);

  const defaultValue = React.useRef<string | undefined>(defaultValueProp);
  const [selectValue, setSelectValue] = React.useState(
    defaultValue.current ?? ''
  );
  const [selectIsOpen, setSelectIsOpen] = React.useState(false);
  // Create a ref to the search input
  const searchInputRef = React.useRef<FilterInputHandle>(null);

  // Keep track of which directory is currently open
  const [openDivisionId, setOpenDivisionId] = React.useState<NodeId | null>(
    null
  );
  // Build a map from each node.id => parent node, so we can "go back" and open offices
  const { parentMap, officeMap } = React.useMemo(() => {
    const map = new Map<NodeId, NodeId | null>();
    const officeMap = new Map<NodeId, NodeId | undefined>();
    function traverse(nodes: PossibleDivisionNode[], parentId: NodeId | null) {
      if (!nodes?.length) {
        return;
      }
      nodes.forEach((node) => {
        map.set(node.id, parentId);
        if (node.children?.length) {
          traverse(node.children, node.id);
        }
        if (node.offices?.length) {
          node.offices.forEach((office) => {
            officeMap.set(office.id, node.id);
          });
        }
      });
    }
    traverse(items, null);

    if (officeMap.size === 0 && items.length > 0) {
      items.forEach((item) => {
        map.set(item.id, ROOT_ID);
        officeMap.set(item.id, ROOT_ID);
      });
    }
    return { parentMap: map, officeMap };
  }, [items]);

  React.useEffect(() => {
    if (defaultValueProp && officeMap) {
      defaultValue.current = defaultValueProp;
      setOpenDivisionId(officeMap.get(defaultValueProp) ?? null);
      setSelectValue(defaultValueProp);
    }
  }, [defaultValueProp, officeMap]);

  React.useEffect(() => {
    if (selectIsOpen) {
      setOpenDivisionId(officeMap.get(selectValue) ?? null);

      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 10);
    } else {
      setTimeout(() => {
        searchInputRef.current?.resetFilter();
      }, 10);
    }
  }, [selectIsOpen]);

  const findById = React.useCallback(
    (id: NodeId): PossibleDivisionNode | null => {
      const queue = [...items];
      while (queue.length) {
        const n = queue.shift()!;
        if (n.id === id) return n;
        if (n.children) {
          queue.push(...n.children);
        }
      }
      return null;
    },
    [items]
  );

  const currentDir = React.useMemo(() => {
    return openDivisionId ? findById(openDivisionId) : null;
  }, [openDivisionId, findById, defaultValue.current]);

  const label = React.useMemo(() => {
    if (selectValue === WL_PRESENCE_REMOTE) {
      return 'Remote';
    }
    if (selectValue === WL_PRESENCE_OOO) {
      return 'Out of Office';
    }
    const directParentId = officeMap.get(selectValue);
    if (!directParentId) {
      if (selectValue.startsWith('offi_')) {
        return items.find((v) => v.id === selectValue)?.name || 'N/A';
      }
      return null;
    }
    if (directParentId === ROOT_ID) {
      return items.find((v) => v.id === selectValue)?.name || 'N/A';
    }
    const parentNode = findById(directParentId);
    if (!parentNode) {
      return null;
    }
    return parentNode.offices.find((v) => v.id === selectValue)?.name || 'N/A';
  }, [selectValue]);

  const handleOpenDir = (dirId: NodeId) => {
    setOpenDivisionId(dirId);
  };

  const handleBack = () => {
    if (openDivisionId == null) return;
    const parentId = parentMap.get(openDivisionId);
    setOpenDivisionId(parentId ?? null);
  };

  const [noFilteredResults, setNoFilteredResults] = React.useState(false);

  return (
    <FilterProvider>
      <SelectGroup>
        <Select
          // open={true}
          onValueChange={setSelectValue}
          onOpenChange={(isOpen) => {
            setSelectIsOpen(isOpen);
          }}
          defaultValue={defaultValueProp}
          {...props}
        >
          <SelectClearTrigger
            className={cn(
              'mr-0 bg-transparent pr-0 font-normal',
              props.triggerClassName
            )}
          >
            {triggerIcon}
            {showSelectedLabel ? label || 'Select an office' : ''}
          </SelectClearTrigger>
          <SelectContent
            scrollViewportClassname={cn('px-0', scrollViewportClassname)}
            variant="dropdown"
            position="popper"
            TopElement={<SelectSearch ref={searchInputRef} />}
            onPointerLeave={(event) => event.preventDefault()}
            onPointerMove={(event) => event.preventDefault()}
            BottomElement={
              props.onClear ? (
                <div className="flex justify-between px-1">
                  <SelectButton
                    onClick={() => {
                      setSelectValue('');
                      props.onClear?.();
                    }}
                  >
                    Clear
                  </SelectButton>
                </div>
              ) : undefined
            }
          >
            <OfficeSelectContent
              allItems={items}
              currentDir={currentDir}
              onOpenDir={handleOpenDir}
              onBack={handleBack}
              setNoFilteredResults={setNoFilteredResults}
            />
            {items.length > 0 &&
            !noFilteredResults &&
            (showRemote || showOOO) ? (
              <SelectSeparator />
            ) : null}
            {showRemote && (
              <FlatOfficeItem
                value={WL_PRESENCE_REMOTE}
                type={LocationVariants.remote}
                label={'Remote'}
                locationFilter={''}
              />
            )}
            {showOOO && (
              <FlatOfficeItem
                value={WL_PRESENCE_OOO}
                type={LocationVariants.OOO}
                label={'Out of Office'}
                locationFilter={''}
              />
            )}
          </SelectContent>
        </Select>
      </SelectGroup>
    </FilterProvider>
  );
};
